/**
 * Web component: Simple teaser (simple-teaser)
 * -----------------------------------------------------------------------------
 * Simple Teaser.
 *
 */
/* eslint no-unused-vars: ['error', { 'argsIgnorePattern': 'component' }] */
import './simple-teaser.scss';

import { vueReady } from '~/core/runners';

import classes from '~/config/classes';
import timings from '~/config/timings';

import { dateTime } from '~/helpers/general';
import * as webComponent from '~/helpers/web-components';
import { checkIfActiveNonPrivate } from '~/helpers/teasers';

/**
 * DOM selectors.
 */
const selectors = {
  tooltip: '[js-simple-teaser="tooltip"]',
  fallback: '[js-simple-teaser="fallback"]',
  teaser: '[js-simple-teaser="teaser"]',
  copyButton: '[js-simple-teaser="copyButton"]',
  promoText: '[js-simple-teaser="promoText"]',
};

/**
 * Create new <simple-teaser> custom element.
 */
class SimpleTeaser extends HTMLElement {
  static observedAttributes = ['start-time', 'end-time'];

  attributeChangedCallback(name, oldValue, newValue) {
    webComponent.handleAttributeUpdate(this, name, newValue, oldValue);
  }

  data(component) {
    return {
      selectors: {
        fallback: component.querySelector(selectors.fallback),
        teasers: [...component.querySelectorAll(selectors.teaser)],
        promoTexts: component.querySelectorAll(selectors.promoText),
        copyButtons: component.querySelectorAll(selectors.copyButton),
      },
    };
  }

  computed(component) {
    return {};
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = this.data(this);
    this.teasersUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teasers if necessary
    const { copyButtons } = this.$data.selectors;
    if (copyButtons) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );
      this.updateTeasers(discountCampaigns);

      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        this.updateTeasers(campaigns);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);
    webComponent.setup(this);

    if (Shopify.designMode) {
      return;
    }

    const { fallback, teasers } = this.$data.selectors;
    let selectedTeaser = this.teaserClosestToEndTime(teasers, null);

    this.addEventListener('click', this.handleCopy);

    /**
     * Set interval to check every second.
     */
    setInterval(() => {
      const oldSelectedTeaser = selectedTeaser;
      selectedTeaser = this.teaserClosestToEndTime(teasers, selectedTeaser);

      if (selectedTeaser === null) {
        this.hideTeasers(teasers);
        this.showFallBack(teasers, fallback);
        return;
      }

      this.showTeaser(selectedTeaser, teasers, fallback);

      if (selectedTeaser === oldSelectedTeaser) {
        return;
      }

      this.handleTrackingCampaign(selectedTeaser, 'view_promotion');

      /**
       * Add click event listener after view event.
       */
      const selectedTeaserLink = selectedTeaser.querySelector(
        selectors.teaserLink,
      );

      if (
        !selectedTeaserLink ||
        selectedTeaserLink.classList.contains('js-hero-teaser-link')
      ) {
        return;
      }

      selectedTeaserLink.addEventListener('click', () => {
        this.handleTrackingCampaign(selectedTeaser, 'select_promotion');
      });
    }, 1000);
  }

  /**
   * Find teaser campaign or closest to end time from current time
   * @param {Array} teasers - Teaser elements.
   * @returns {Element} - Teaser element or null.
   */
  teaserClosestToEndTime(teasers, selectedTeaser = null) {
    let returnTeaser = null;

    if (selectedTeaser !== null) {
      const validTime = dateTime(
        selectedTeaser.dataset.start,
        selectedTeaser.dataset.end,
      );

      if (validTime) {
        returnTeaser = selectedTeaser;
      }
    }

    /**
     * Iterate over teasers to find closest to end time.
     */
    for (const teaser of teasers) {
      const validTime = dateTime(teaser.dataset.start, teaser.dataset.end);

      if (!validTime) {
        if (returnTeaser === teaser) {
          returnTeaser = null;
        }

        continue;
      }

      /**
       * Return teaser if it has no end date (never expires).
       */
      if (!teaser.dataset?.end || teaser.dataset?.end === 'false') {
        returnTeaser = teaser;
        continue;
      }

      const teaserTimeToStart = new Date(teaser.dataset.start);
      const teaserTimeToEnd = new Date(teaser.dataset.end);
      const currentTime = new Date();

      returnTeaser =
        currentTime >= teaserTimeToStart && currentTime <= teaserTimeToEnd
          ? teaser
          : null;
    }

    /**
     * Return teaser, if none found return null.
     */
    return returnTeaser;
  }

  /**
   * Shows teasers banner.
   * @param {Array} teasers - Teaser elements.
   * @param {Element} fallback - Fallback element.
   */
  hideTeasers(teasers) {
    for (const teaser of teasers) {
      teaser.classList.add(classes.hidden);
    }
  }

  /**
   * Shows fallback if all teasers are hidden.
   * @param {Array} teasers - Teaser elements.
   * @param {Element} fallback - Fallback element.
   */
  showFallBack(teasers, fallback) {
    const allTeasersHidden = teasers.every((teaser) =>
      teaser.classList.contains('is-hidden'),
    );

    if (!allTeasersHidden) {
      return;
    }

    if (fallback) {
      fallback.classList.remove(classes.hidden);
    }
  }

  /**
   * Shows current teaser banner.
   * @param {Element} teaser - Teaser element.
   * @param {Element} fallback - Fallback  element.
   */
  showTeaser(teaser, teasers, fallback) {
    teasers.forEach((teaserImage) => {
      teaserImage.classList.add(classes.hidden);
    });

    teaser.classList.remove(classes.hidden);

    if (fallback) {
      fallback.classList.add(classes.hidden);
    }
  }

  /**
   * Update teaser content based on campaign store state (code applied or not).
   * There can be multiple teasers incl. one fallback. Update all.
   * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
   * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
   */
  updateTeasers(campaigns) {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { promoTexts, copyButtons } = this.$data.selectors;

    if (!copyButtons) {
      return;
    }

    const copyButtonIcons = this.querySelectorAll(
      `${selectors.copyButton} svg`,
    );

    if (checkIfActiveNonPrivate(campaigns)) {
      const copyButtonTexts = this.querySelectorAll(
        `${selectors.copyButton} span:first-child`,
      );

      copyButtons.forEach((copyButton) => {
        copyButton.disabled = true;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'none';
      });

      copyButtonTexts.forEach((copyButtonText) => {
        copyButtonText.innerText = campaigns.active.code[0];
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.codeApplied;
      });

      this.teasersUpdated = true;
    } else if (this.teasersUpdated) {
      // reset everything to initial state
      copyButtons.forEach((copyButton) => {
        const copyButtonText = copyButton.querySelector('span');

        copyButton.disabled = false;
        copyButtonText.innerText = copyButton.dataset.code;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'unset';
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.text;
      });

      this.teasersUpdated = false;
    }
  }

  /**
   * Copy promo code and toggle tooltip.
   * @param {Event} event - Click event.
   */
  handleCopy(event) {
    if (!event.target.matches(selectors.copyButton)) {
      return;
    }

    const copyButton = event.target;
    const tooltip = event.target.querySelector(selectors.tooltip);

    if (!copyButton) {
      return;
    }

    const code = copyButton.dataset.code;
    navigator.clipboard.writeText(code);

    if (!tooltip) {
      return;
    }

    tooltip.classList.add(classes.active);

    setTimeout(() => {
      tooltip.classList.remove(classes.active);
    }, timings.message);
  }

  /**
   * Handle campaign view for dataLayer.
   * @param {HTMLElement} teaser - Teaser item.
   * @param {String} eventType - Event type:
   * (view_promotion or select_promotion).
   */
  handleTrackingCampaign(campaign, eventType = 'view_promotion') {
    cnvs.EventBus.emit(`dataLayer:${eventType}`, {
      promotion: {
        promotion_id: campaign.dataset.campaignId
          ? campaign.dataset.campaignId
          : '',
        promotion_name: campaign.dataset.campaignName
          ? campaign.dataset.campaignName
          : '',
        creative_slot: 'campaign-simple-teaser',
        creative_name: 'Campaign simple teaser',
      },
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('simple-teaser')) {
    return;
  }

  customElements.define('simple-teaser', SimpleTeaser);
});
